import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { type IFile } from "src/interfaces";
import { getUserId } from "src/utils";
import {
  getPendingRequestAction,
  getConsolidatedChecklistAction,
  readPendingBody,
} from "src/utils/getPendingRequestLabel";
import {
  getPendingRequestRoute,
  indexEntryIdTypes,
  type EntryIdType,
} from "src/utils/getPendingRequestRoute";

export interface IPendingRequest {
  req: string;
  name: string;
  data: string;
  status: string;
  created: number;
  uploaded?: number | null;
  error?: string | null;
  action: string;
  lbname?: string;
  route?: string | null;
  userid?: string | null;
  serverEntryId?: string | null;
  /** Queued by a different user, so this session must not replay it. */
  isOtherUser?: boolean;
  /** Rows collapsed into this one, when more than one is queued. */
  queuedCount?: number;
  /** What those rows are, for the queued-count badge. */
  queuedNoun?: string;
}

interface IPendingNewEntry {
  id: string;
  lbid: string;
  created: number;
}

interface IPendingFiles {
  entryId: string;
  files: IFile[];
}

interface PendingRequestsSlice {
  pendingRequests: IPendingRequest[];
  pendingWithdraws: string[];
  pendingNewEntries: IPendingNewEntry[];
  pendingFiles: IPendingFiles[];
  pendingVerify: Array<{
    lbeid: string | null;
    partid: string | null;
    meid: string | null;
  }>;
}

const initialState: PendingRequestsSlice = {
  pendingRequests: [],
  pendingWithdraws: [],
  pendingNewEntries: [],
  pendingFiles: [],
  pendingVerify: [],
};

const byCreatedDesc = <T extends { created: number }>(items: T[]) =>
  [...items].sort((a, b) => b.created - a.created);

const isActiveUploadStatus = (status: string) => status !== "done";

// Ticking a long checklist or saving repeatedly offline queues a row each
// time, so these collapse per entry. Keyed by request type as well, to keep an
// entry's saves and its checklist actions on separate rows.
const CONSOLIDATED_REQUESTS: Record<string, string> = {
  "save-entry": "saves",
  "checklist-action": "actions",
};

// The representative row is the oldest not yet `done`, which is the one the
// replay chain attempts next and the one holding the entry up when it has
// failed, shown against the newest row's timestamp.
const consolidateByEntry = (
  requests: IPendingRequest[],
  entryIdTypeByKey: Map<string, EntryIdType>,
) => {
  const groups = new Map<string, IPendingRequest[]>();
  const output: IPendingRequest[] = [];

  requests.forEach(request => {
    const [name, entryId] = request.req.split("__");

    if (!CONSOLIDATED_REQUESTS[name] || !entryId) {
      output.push(request);
      return;
    }

    const cid =
      name === "checklist-action"
        ? readPendingBody(request.data)?.params?.cid
        : undefined;
    const key = cid ? `${name}__${entryId}__${cid}` : `${name}__${entryId}`;

    const group = groups.get(key);
    if (group) {
      group.push(request);
      return;
    }

    groups.set(key, [request]);
  });

  groups.forEach(group => {
    if (group.length === 1) {
      output.push(group[0]);
      return;
    }

    const oldestFirst = [...group].sort((a, b) => a.created - b.created);
    const newest = oldestFirst[oldestFirst.length - 1];
    const blocking =
      oldestFirst.find(request => request.status !== "done") ?? newest;
    const serverEntryId =
      group.find(request => request.serverEntryId)?.serverEntryId ??
      blocking.serverEntryId;
    const requestType = group[0].req.split("__")[0];
    const displayAction =
      requestType === "checklist-action"
        ? getConsolidatedChecklistAction(group)
        : undefined;

    output.push({
      ...blocking,
      action: displayAction ?? blocking.action,
      serverEntryId,
      route: getPendingRequestRoute({
        req: blocking.req,
        data: blocking.data,
        server_entry_id: serverEntryId,
        local_entry_id: (blocking as RawPendingUpload).local_entry_id,
        entryIdTypeByKey,
      }),
      created: newest.created,
      uploaded: newest.uploaded,
      queuedCount: group.length,
      queuedNoun: CONSOLIDATED_REQUESTS[group[0].req.split("__")[0]],
    });
  });

  return output;
};

const filterDisplayRequests = (
  requests: IPendingRequest[],
  entryIdTypeByKey: Map<string, EntryIdType>,
) => byCreatedDesc(consolidateByEntry(requests, entryIdTypeByKey));

interface RawPendingUpload extends IPendingRequest {
  server_entry_id?: string | null;
  local_entry_id?: string | null;
}

const indexServerEntryIds = (rows: RawPendingUpload[]) => {
  const byLocalEntryId = new Map<string, string>();

  rows.forEach(row => {
    if (row.local_entry_id && row.server_entry_id) {
      byLocalEntryId.set(row.local_entry_id, row.server_entry_id);
    }
  });

  return byLocalEntryId;
};

const resolveServerEntryId = (
  pending: RawPendingUpload,
  byLocalEntryId: Map<string, string>,
) =>
  pending.server_entry_id ??
  (pending.local_entry_id
    ? byLocalEntryId.get(pending.local_entry_id)
    : undefined) ??
  null;

export const pendingRequestsSlice = createSlice({
  name: "verifySlice",
  initialState,
  reducers: {
    setPendingRequests: (state, action: PayloadAction<RawPendingUpload[]>) => {
      const pendingRequests: IPendingRequest[] = [];
      const pendingWithdraws: string[] = [];
      const pendingNewEntries: IPendingNewEntry[] = [];
      const newEntriesIds: string[] = [];
      const pendingFiles: IPendingFiles[] = [];
      let pendingVerify: Array<{
        lbeid: string | null;
        partid: string | null;
        meid: string | null;
      }> = [];

      const serverEntryIdByLocalEntryId = indexServerEntryIds(action.payload);
      const entryIdTypeByKey = indexEntryIdTypes(action.payload);

      action.payload.forEach(pending => {
        let body: Record<string, any>;
        try {
          body = JSON.parse(pending.data);
        } catch {
          return;
        }

        const splitted = pending.req.split("__");
        const request = splitted[0];
        const id = splitted[1];
        const isActive = isActiveUploadStatus(pending.status);
        const isOtherUser =
          Boolean(pending.userid) && pending.userid !== getUserId();
        const serverEntryId = resolveServerEntryId(
          pending,
          serverEntryIdByLocalEntryId,
        );

        pendingRequests.push({
          ...pending,
          serverEntryId,
          action: getPendingRequestAction(request, body),
          lbname: body._lbname,
          route: getPendingRequestRoute({
            req: pending.req,
            data: pending.data,
            server_entry_id: serverEntryId,
            local_entry_id: pending.local_entry_id,
            entryIdTypeByKey,
          }),
          isOtherUser,
        });

        if (!isActive) {
          return;
        }

        // The overlays below drive this user's UI - a withdrawn-looking entry,
        // a phantom entry in the picker - so another user's work is excluded.
        if (isOtherUser) {
          return;
        }

        if (!id) {
          return;
        }

        if (request === "withdraw-entry") {
          pendingWithdraws.push(id);
        } else if (request === "save-entry") {
          if (id.slice(0, 4) === "new=") {
            const lbid = id.split("&&")[1]?.slice(5);
            if (lbid && !newEntriesIds.includes(id)) {
              pendingNewEntries.push({
                id,
                lbid,
                created: pending.created,
              });
              newEntriesIds.push(id);
            }
          }
        } else if (request === "upload-file") {
          const entryId = body.meid ?? body.partid ?? body.lbeid;
          const files = (body.files ?? []).map((f: { name: string; type: string }) => ({
            aid: Math.random(),
            date: new Date(pending.created),
            filename: f.name,
            type: f.type,
            deleted: 0,
            local: true,
          }));
          pendingFiles.push({ entryId, files });
        } else if (request === "verify-entry") {
          if (body.params?.user === getUserId()) {
            pendingVerify = [...pendingVerify, ...(body.params.list ?? [])];
          }
        }
      });
      state.pendingRequests = filterDisplayRequests(
        pendingRequests,
        entryIdTypeByKey,
      );
      state.pendingWithdraws = pendingWithdraws;
      state.pendingNewEntries = byCreatedDesc(pendingNewEntries);
      state.pendingFiles = pendingFiles;
      state.pendingVerify = pendingVerify;
    },
  },
});

export const { setPendingRequests } = pendingRequestsSlice.actions;

export default pendingRequestsSlice.reducer;
